import {addTypenameToDocument} from "apollo-utilities";
import {print} from "graphql/language/printer";

import {documentNode, type GqlOperation} from "@khan/data-gql-core";

const _memoizedGqlOps = new Map<GqlOperation<any, any>, string>();

/**
 * Clear any memoized strings.
 */
export const clearNormalizedOperationStringsCache = () => {
    _memoizedGqlOps.clear();
};

/**
 * Get the normalized string representation of a given `GqlOperation`.
 *
 * Printing the document to create a query string is quite
 * expensive so we memoize it.  This relies on calls to graphql-tag's 'gql'
 * being made once at the top-level of a file outside of the body of the
 * component.  If 'gql' is being called inside of the component we'll end
 * up with a different copy of the operation each time which would be a
 * different key.
 *
 * @param operation The operation being normalized into a document node.
 * @returns The normalized document node.
 */
export const asNormalizedOperationString = <
    TData,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
): string => {
    const cachedOpString = _memoizedGqlOps.get(operation);
    if (cachedOpString) {
        return cachedOpString;
    }

    // For now, to make sure we have parity with the Apollo way, we need to
    // modify the document to add the typename. If we don't do this, the hash
    // created for fastly cached queries will be wrong and the backend will
    // reject our requests.
    const document = addTypenameToDocument(documentNode(operation));
    const opString = print(document);

    _memoizedGqlOps.set(operation, opString);
    return opString;
};
